import { useState, useLayoutEffect } from "react";

// Constants
const TEXTAREA_MIN_HEIGHT = 36; // Height for single-line mode (px)
const TEXTAREA_MAX_HEIGHT = 330; // Maximum height for expanded mode (~15 lines)
const MULTILINE_THRESHOLD = 50; // Character count before considering content multi-line

interface UseCompactExpansionOptions {
  value: string;
  mode: "search" | "chat";
  isFocused: boolean;
  hasContext: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

interface UseCompactExpansionResult {
  shouldExpand: boolean;
  collapsedWithContent: boolean;
  setShouldExpand: (value: boolean) => void;
  setCollapsedWithContent: (value: boolean) => void;
  hasMultiLineContent: boolean;
}

/**
 * Hook to manage expansion/collapse behavior for CompactUnifiedSearch
 * Handles the complex state transitions between focused/unfocused and expanded/collapsed states
 */
export function useCompactExpansion({
  value,
  mode,
  isFocused,
  hasContext,
  textareaRef,
}: UseCompactExpansionOptions): UseCompactExpansionResult {
  const [shouldExpand, setShouldExpand] = useState(false);
  const [collapsedWithContent, setCollapsedWithContent] = useState(false);

  // Calculate if content is multi-line
  const hasMultiLineContent =
    value.includes("\n") ||
    (value.length > MULTILINE_THRESHOLD && value.trim() !== "");

  // Check if content needs expansion - using useLayoutEffect to prevent visual jumps
  useLayoutEffect(() => {
    // Search mode: always collapsed
    if (mode === "search") {
      setShouldExpand(false);
      setCollapsedWithContent(false);
      return;
    }

    // Chat mode expansion logic
    if (mode === "chat") {
      const hasSignificantContent = hasMultiLineContent || hasContext;

      // When not focused
      if (!isFocused) {
        if (hasSignificantContent) {
          setShouldExpand(false);
          setCollapsedWithContent(true);
          return;
        }
        setShouldExpand(false);
        setCollapsedWithContent(false);
        return;
      }

      // When focused
      if (isFocused) {
        if (collapsedWithContent || hasContext || hasMultiLineContent) {
          setShouldExpand(true);
          setCollapsedWithContent(false);
          return;
        }
        setShouldExpand(false);
        setCollapsedWithContent(false);
      }
    }
  }, [value, mode, hasContext, isFocused, collapsedWithContent, hasMultiLineContent]);

  // Auto-resize textarea height - using useLayoutEffect for immediate DOM updates
  useLayoutEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;

    if (mode === "search" || !shouldExpand) {
      // Single line for search or compact chat
      textarea.style.height = `${TEXTAREA_MIN_HEIGHT}px`;
      textarea.style.overflowY = "hidden";
    } else {
      // Multi-line for expanded chat
      const maxHeight = TEXTAREA_MAX_HEIGHT;
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      textarea.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
    }
  }, [value, mode, shouldExpand, textareaRef]);

  return {
    shouldExpand,
    collapsedWithContent,
    setShouldExpand,
    setCollapsedWithContent,
    hasMultiLineContent,
  };
}

// Export constants for use in other components
export { MULTILINE_THRESHOLD };